import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import './DailyPuzzle.css';
import SharePuzzle from './SharePuzzle';


const gridSize = 5;

const styles = {
  graffitiBanner: {
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '4rem',
    fontWeight: 600,
    color: '#ff3cac',
    textShadow: '2px 2px 5px #000',
    zIndex: 9999,
    pointerEvents: 'none',
    textAlign: 'center'
  },

  wrapper: {
  position: 'relative',
  display: 'inline-block',
  margin: '0 auto',
  width: '100%',
  maxWidth: '600px'
}
};

const DailyPuzzle = () => {
  const [tiles, setTiles] = useState([]);
  const [correctTiles, setCorrectTiles] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [message, setMessage] = useState('');
  const [naturalSize, setNaturalSize] = useState({ width: 1, height: 1 });
  const [selectedTile, setSelectedTile] = useState(null);
  const [coins, setCoins] = useState(() => parseInt(localStorage.getItem('coins')) || 500);
  const [showGraffiti, setShowGraffiti] = useState(false);
  const [hasCelebrated, setHasCelebrated] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const containerRef = useRef(null);

  const puzzleComplete = initialLoadComplete && tiles.every((val, idx) => val === correctTiles[idx]);

  useEffect(() => {
    const fetchPuzzle = async () => {
      const db = getFirestore();
      const today = new Date();
      const todayKey = today.toLocaleDateString('en-CA');
      const docSnap = await getDoc(doc(db, 'puzzleSchedule', todayKey));

      if (docSnap.exists()) {
        const data = docSnap.data();
        setMessage(data.message || '');

        if (data.imagePath) {
          try {
            const storage = getStorage();
            const imageRef = ref(storage, data.imagePath);
            const url = await getDownloadURL(imageRef);

            const img = new Image();
            img.onload = () => setNaturalSize({ width: img.width, height: img.height });
            img.src = url;

            setImageUrl(url);
          } catch (err) {
            console.error("❌ Failed to fetch image from Firebase Storage:", err);
          }
        } else {
          console.error("⚠️ No imagePath provided in Firestore document.");
        }
      }
    };

    fetchPuzzle();
  }, []);

  useEffect(() => {
    if (imageUrl) {
      const indices = Array.from({ length: gridSize * gridSize }, (_, i) => i);
      let shuffled;
      do {
        shuffled = [...indices];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
      } while (shuffled.every((val, idx) => val === idx));

      setTiles(shuffled);
      setCorrectTiles(indices);
      setHasCelebrated(false);
      setInitialLoadComplete(true);
    }
  }, [imageUrl]);

  const handleClick = (clickedIdx) => {
    if (selectedTile === null) {
      setSelectedTile(clickedIdx);
      return;
    }

    if (selectedTile === clickedIdx) {
      setSelectedTile(null);
      return;
    }

    const newTiles = [...tiles];
    [newTiles[selectedTile], newTiles[clickedIdx]] = [newTiles[clickedIdx], newTiles[selectedTile]];
    setTiles(newTiles);
    setSelectedTile(null);
    setCoins((prev) => Math.max(prev - 5, 0));
  };

  useEffect(() => {
    localStorage.setItem('coins', coins);
    window.dispatchEvent(new Event('storage'));
  }, [coins]);

  useEffect(() => {
    if (puzzleComplete && !hasCelebrated) {
      setCoins((prev) => prev + 100);
      setShowGraffiti(true);
      setHasCelebrated(true);
      setTimeout(() => setShowGraffiti(false), 3000);
    }
  }, [puzzleComplete, hasCelebrated]);

  return (
    <main className="daily-puzzle">
      <h1>Daily Puzzle</h1>
      {imageUrl && (
        <div style={styles.wrapper}>
        <div
          ref={containerRef}
          className="puzzle-grid"
          style={{
            aspectRatio: `${naturalSize.width} / ${naturalSize.height}`,
            width: '100%',
            maxWidth: '600px',
            margin: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            border: puzzleComplete ? 'none' : '1px solid #ddd'
          }}
        >
          {tiles.map((val, i) => (
            <div
              key={i}
              className="tile"
              onClick={() => handleClick(i)}
              style={{
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                backgroundPosition: `${(val % gridSize) * 100 / (gridSize - 1)}% ${Math.floor(val / gridSize) * 100 / (gridSize - 1)}%`,
                width: `${100 / gridSize}%`,
                height: `${100 / gridSize}%`,
                border: puzzleComplete ? 'none' : val === i ? '2px solid limegreen' : '1px solid #ccc',
                boxSizing: 'border-box'
              }}
            />
          ))}
        </div>
       </div> 
      )}
      {puzzleComplete && <div className="message-box">{message}</div>}
      <SharePuzzle puzzleUrl={`https://thatpuzzleapp.com/DailyPuzzle`} />

      <AnimatePresence>
        {showGraffiti && (
          <motion.div
            style={styles.graffitiBanner}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1.1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 1.5 }}
          >
            🎉 Winner winner chicken dinner! 🎉
          </motion.div>
        )}
      </AnimatePresence>

      {showGraffiti && <Confetti numberOfPieces={300} recycle={false} />}
    </main>
  );
};

export default DailyPuzzle;
